<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [phoneNumber]="phoneNumber"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">
  
  <div class="content-section">
    <h2>L'assurance décennale : protection obligatoire pour les constructeurs</h2>
    <p>
      L'<strong>assurance décennale</strong> (ou garantie décennale) est une assurance <strong>obligatoire</strong> 
      pour tous les professionnels du bâtiment (artisans, entreprises, architectes, maîtres d'œuvre...). 
      Elle couvre pendant <strong>10 ans</strong> les dommages qui compromettent la solidité de l'ouvrage 
      ou le rendent impropre à sa destination.
    </p>
    <p>
      Sans cette assurance, vous ne pouvez légalement exercer. En cas de sinistre, votre responsabilité personnelle 
      peut être engagée pour des montants très élevés. C'est une protection <strong>indispensable</strong> 
      pour sécuriser votre activité et votre patrimoine.
    </p>
  </div>

  <div class="content-section bg-warning">
    <div class="alert-obligation">
      <i class="pi pi-exclamation-triangle"></i>
      <div>
        <h3>⚠️ Obligation légale</h3>
        <p>
          Depuis la loi Spinetta de 1978, l'assurance décennale est <strong>obligatoire</strong> 
          dès l'ouverture de votre chantier. Exercer sans assurance décennale est passible de :
        </p>
        <ul>
          <li><strong>6 mois d'emprisonnement</strong></li>
          <li><strong>75 000€ d'amende</strong></li>
          <li>Interdiction d'exercer</li>
          <li>Responsabilité personnelle illimitée en cas de sinistre</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Qui doit souscrire une décennale ?</h2>
    <div class="professionals-list">
      <div class="pro-category">
        <h3>🔨 Artisans et entreprises du bâtiment</h3>
        <div class="professions-grid">
          <div class="profession">
            <i class="pi pi-home"></i>
            <span>Maçons</span>
          </div>
          <div class="profession">
            <i class="pi pi-bolt"></i>
            <span>Électriciens</span>
          </div>
          <div class="profession">
            <i class="pi pi-cog"></i>
            <span>Plombiers</span>
          </div>
          <div class="profession">
            <i class="pi pi-building"></i>
            <span>Couvreurs</span>
          </div>
          <div class="profession">
            <i class="pi pi-th-large"></i>
            <span>Menuisiers</span>
          </div>
          <div class="profession">
            <i class="pi pi-palette"></i>
            <span>Peintres</span>
          </div>
          <div class="profession">
            <i class="pi pi-desktop"></i>
            <span>Chauffagistes</span>
          </div>
          <div class="profession">
            <i class="pi pi-tablet"></i>
            <span>Carreleurs</span>
          </div>
        </div>
      </div>

      <div class="pro-category">
        <h3>📐 Concepteurs et maîtres d'œuvre</h3>
        <div class="professions-grid">
          <div class="profession">
            <i class="pi pi-map"></i>
            <span>Architectes</span>
          </div>
          <div class="profession">
            <i class="pi pi-compass"></i>
            <span>Bureaux d'études</span>
          </div>
          <div class="profession">
            <i class="pi pi-pencil"></i>
            <span>Économistes de la construction</span>
          </div>
          <div class="profession">
            <i class="pi pi-check-square"></i>
            <span>Maîtres d'œuvre</span>
          </div>
        </div>
      </div>

      <div class="pro-category">
        <h3>⚙️ Installateurs et poseurs</h3>
        <div class="professions-grid">
          <div class="profession">
            <i class="pi pi-sun"></i>
            <span>Poseurs panneaux solaires</span>
          </div>
          <div class="profession">
            <i class="pi pi-window-maximize"></i>
            <span>Installateurs menuiseries</span>
          </div>
          <div class="profession">
            <i class="pi pi-shield"></i>
            <span>Poseurs d'isolation</span>
          </div>
          <div class="profession">
            <i class="pi pi-sliders-h"></i>
            <span>Installateurs ascenseurs</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section bg-light">
    <h2>Tarification selon l'activité</h2>
    <div class="pricing-by-activity">
      <div class="activity-pricing">
        <div class="activity-header low-risk">
          <h3>✅ Risque faible</h3>
          <p class="price-range">500€ à 2 000€/an</p>
        </div>
        <ul>
          <li><strong>Peinture intérieure :</strong> 500€ - 1 200€/an</li>
          <li><strong>Plâtrerie :</strong> 600€ - 1 500€/an</li>
          <li><strong>Carrelage :</strong> 700€ - 1 800€/an</li>
          <li><strong>Revêtements de sol :</strong> 550€ - 1 400€/an</li>
        </ul>
      </div>

      <div class="activity-pricing">
        <div class="activity-header medium-risk">
          <h3>⚠️ Risque moyen</h3>
          <p class="price-range">1 500€ à 4 000€/an</p>
        </div>
        <ul>
          <li><strong>Électricité :</strong> 1 500€ - 3 500€/an</li>
          <li><strong>Plomberie :</strong> 1 800€ - 4 000€/an</li>
          <li><strong>Menuiserie (portes/fenêtres) :</strong> 1 600€ - 3 800€/an</li>
          <li><strong>Chauffage :</strong> 1 700€ - 3 600€/an</li>
          <li><strong>Climatisation :</strong> 1 600€ - 3 400€/an</li>
        </ul>
      </div>

      <div class="activity-pricing">
        <div class="activity-header high-risk">
          <h3>🔴 Risque élevé</h3>
          <p class="price-range">3 000€ à 10 000€+/an</p>
        </div>
        <ul>
          <li><strong>Maçonnerie gros œuvre :</strong> 3 000€ - 8 000€/an</li>
          <li><strong>Couverture / Toiture :</strong> 3 500€ - 10 000€/an</li>
          <li><strong>Charpente :</strong> 3 200€ - 9 000€/an</li>
          <li><strong>Terrassement / Fondations :</strong> 4 000€ - 12 000€/an</li>
          <li><strong>Construction maisons individuelles :</strong> 5 000€ - 15 000€/an</li>
        </ul>
      </div>

      <div class="activity-pricing">
        <div class="activity-header architect-risk">
          <h3>📐 Concepteurs</h3>
          <p class="price-range">2 000€ à 8 000€+/an</p>
        </div>
        <ul>
          <li><strong>Architecte débutant :</strong> 2 000€ - 4 000€/an</li>
          <li><strong>Architecte expérimenté :</strong> 3 500€ - 6 000€/an</li>
          <li><strong>Bureau d'études structure :</strong> 3 000€ - 8 000€/an</li>
          <li><strong>Maître d'œuvre :</strong> 2 500€ - 5 500€/an</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Critères d'évaluation de la prime</h2>
    <div class="premium-criteria">
      <div class="criterion">
        <h3>📊 Chiffre d'affaires</h3>
        <p>Plus votre CA est élevé, plus la prime augmente (tarif souvent 3% à 7% du CA).</p>
        <div class="ca-examples">
          <div class="ca-example">
            <span>CA &lt; 50k€</span>
            <span class="price">500€ - 1 500€</span>
          </div>
          <div class="ca-example">
            <span>CA 50k€ - 150k€</span>
            <span class="price">1 500€ - 5 000€</span>
          </div>
          <div class="ca-example">
            <span>CA 150k€ - 500k€</span>
            <span class="price">5 000€ - 15 000€</span>
          </div>
          <div class="ca-example">
            <span>CA &gt; 500k€</span>
            <span class="price">15 000€+</span>
          </div>
        </div>
      </div>

      <div class="criterion">
        <h3>🎓 Expérience et diplômes</h3>
        <ul>
          <li><strong>Auto-entrepreneur débutant (&lt; 2 ans) :</strong> Surprime +20% à +50%</li>
          <li><strong>Artisan avec 5-10 ans d'expérience :</strong> Tarif normal</li>
          <li><strong>Entreprise + de 10 ans sans sinistre :</strong> Réduction -10% à -20%</li>
          <li><strong>Qualifications (RGE, Qualibat) :</strong> Réduction -5% à -15%</li>
          <li><strong>Diplômes reconnus (CAP, BEP, BTS) :</strong> Tarif plus favorable</li>
        </ul>
      </div>

      <div class="criterion">
        <h3>📜 Historique de sinistres</h3>
        <ul>
          <li><strong>Aucun sinistre 5 dernières années :</strong> Bonus -10% à -20%</li>
          <li><strong>1 sinistre déclaré :</strong> Malus +20% à +40%</li>
          <li><strong>2 sinistres ou plus :</strong> Malus +50% à +100% (voire refus)</li>
          <li><strong>Sinistres graves (effondrement) :</strong> Difficultés à s'assurer, primes très élevées</li>
        </ul>
      </div>

      <div class="criterion">
        <h3>🔧 Type de chantiers</h3>
        <ul>
          <li><strong>Rénovation simple :</strong> Risque faible (tarif de base)</li>
          <li><strong>Construction neuve :</strong> Risque moyen (+10% à +30%)</li>
          <li><strong>Travaux en hauteur (immeubles) :</strong> Risque élevé (+30% à +50%)</li>
          <li><strong>Chantiers publics/ERP :</strong> Exigences élevées (+20% à +40%)</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section bg-accent">
    <h2>Que couvre la garantie décennale ?</h2>
    <div class="coverage-details">
      <div class="coverage-yes">
        <h3>✅ Dommages couverts</h3>
        <ul>
          <li><strong>Solidité de l'ouvrage compromise :</strong> fissures importantes, affaissement, effondrement</li>
          <li><strong>Impropriété à destination :</strong> local inhabitable ou inutilisable pour son usage prévu</li>
          <li><strong>Éléments indissociables :</strong> menuiseries extérieures, installations fixes (plomberie, électricité, chauffage intégrés)</li>
          <li><strong>Dommages aux éléments d'équipement :</strong> si leur défaillance rend l'ouvrage impropre à destination (ex: chaudière, ascenseur)</li>
          <li><strong>Exemples concrets :</strong>
            <ul>
              <li>Infiltrations d'eau suite à défaut de toiture</li>
              <li>Fissures importantes menaçant la structure</li>
              <li>Défaut d'étanchéité rendant les locaux inhabitables</li>
              <li>Effondrement de plancher ou mur porteur</li>
              <li>Installation électrique défectueuse créant un danger</li>
            </ul>
          </li>
        </ul>
      </div>

      <div class="coverage-no">
        <h3>❌ Dommages non couverts</h3>
        <ul>
          <li><strong>Dommages esthétiques :</strong> fissures superficielles, défauts de peinture</li>
          <li><strong>Usure normale :</strong> vétusté, défaut d'entretien</li>
          <li><strong>Éléments mobiliers :</strong> meubles, appareils non fixés</li>
          <li><strong>Dommages apparents à réception :</strong> défauts constatés lors de la livraison (couverts par garantie de parfait achèvement)</li>
          <li><strong>Travaux hors contrat :</strong> prestations non mentionnées dans le devis/contrat</li>
          <li><strong>Catastrophes naturelles :</strong> tremblements de terre, inondations (couverts par autres assurances)</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Durée et déclenchement de la garantie</h2>
    <div class="timeline-decennale">
      <div class="timeline-item">
        <div class="timeline-marker start">📅</div>
        <div class="timeline-content">
          <h4>Début des travaux</h4>
          <p><strong>Assurance obligatoire avant l'ouverture du chantier</strong></p>
          <p>Vous devez fournir l'attestation d'assurance décennale au maître d'ouvrage.</p>
        </div>
      </div>

      <div class="timeline-item">
        <div class="timeline-marker">🏗️</div>
        <div class="timeline-content">
          <h4>Pendant les travaux</h4>
          <p>L'assurance est active pendant toute la durée du chantier.</p>
        </div>
      </div>

      <div class="timeline-item">
        <div class="timeline-marker key">✅</div>
        <div class="timeline-content">
          <h4>Réception des travaux</h4>
          <p><strong>Point de départ de la garantie décennale (10 ans)</strong></p>
          <p>La réception marque la livraison de l'ouvrage et l'acceptation par le maître d'ouvrage.</p>
        </div>
      </div>

      <div class="timeline-item">
        <div class="timeline-marker">⏳</div>
        <div class="timeline-content">
          <h4>Années 1 à 10</h4>
          <p><strong>Période de couverture décennale</strong></p>
          <p>En cas de dommage relevant de la décennale, votre assurance prend en charge les réparations.</p>
        </div>
      </div>

      <div class="timeline-item">
        <div class="timeline-marker end">🏁</div>
        <div class="timeline-content">
          <h4>Fin de garantie (10 ans après réception)</h4>
          <p>La garantie décennale expire. Seule l'usure normale subsiste.</p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section bg-light">
    <h2>Exemple de calcul de prime</h2>
    <div class="example-calculation">
      <div class="example-header">
        <h3>🔨 Artisan électricien - Auto-entrepreneur</h3>
        <p><strong>Activité :</strong> Installation électrique résidentielle | <strong>CA annuel :</strong> 80 000€ | <strong>Expérience :</strong> 3 ans | <strong>Aucun sinistre</strong></p>
      </div>
      <div class="calc-details">
        <div class="calc-line">
          <span>Prime de base (électricité, risque moyen)</span>
          <span>2 500€/an</span>
        </div>
        <div class="calc-line">
          <span>Coefficient CA (80k€)</span>
          <span>×1,2</span>
        </div>
        <div class="calc-line subtotal">
          <span>Sous-total</span>
          <span>3 000€/an</span>
        </div>
        <div class="calc-line reduction">
          <span>- Qualification RGE (-10%)</span>
          <span>-300€/an</span>
        </div>
        <div class="calc-line reduction">
          <span>- Aucun sinistre (-10%)</span>
          <span>-300€/an</span>
        </div>
        <div class="calc-line total">
          <span><strong>Prime annuelle totale</strong></span>
          <span class="final-price">2 400€/an</span>
        </div>
        <div class="calc-line monthly">
          <span>Soit en mensualités</span>
          <span>200€/mois</span>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Comment réduire le coût de votre décennale ?</h2>
    <div class="cost-reduction-tips">
      <div class="tip">
        <i class="pi pi-check-circle"></i>
        <div>
          <h4>Obtenez des qualifications</h4>
          <p>RGE, Qualibat, QualiPV... Ces labels rassurent les assureurs et réduisent votre prime de 5% à 15%.</p>
        </div>
      </div>
      <div class="tip">
        <i class="pi pi-chart-line"></i>
        <div>
          <h4>Limitez votre CA déclaré</h4>
          <p>La prime est calculée sur le CA. Déclarez uniquement le CA soumis à garantie décennale (excluez fourniture de matériel seul, etc.).</p>
        </div>
      </div>
      <div class="tip">
        <i class="pi pi-shield"></i>
        <div>
          <h4>Maintenez un historique sans sinistre</h4>
          <p>Travaillez avec soin pour éviter les sinistres. Un bon historique permet des réductions jusqu'à -20%.</p>
        </div>
      </div>
      <div class="tip">
        <i class="pi pi-users"></i>
        <div>
          <h4>Regroupez-vous (assurance de groupe)</h4>
          <p>Certaines fédérations professionnelles négocient des tarifs de groupe avantageux pour leurs adhérents.</p>
        </div>
      </div>
      <div class="tip">
        <i class="pi pi-book"></i>
        <div>
          <h4>Formez-vous régulièrement</h4>
          <p>Formations continues, mise à jour des normes (NF C 15-100, RE2020...). Cela valorise votre profil.</p>
        </div>
      </div>
      <div class="tip">
        <i class="pi pi-search"></i>
        <div>
          <h4>Comparez les offres chaque année</h4>
          <p>Les tarifs varient fortement d'un assureur à l'autre (jusqu'à 50% d'écart). Faites jouer la concurrence !</p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section cta-section">
    <h2>Trouvez votre assurance décennale au meilleur prix</h2>
    <p>Comparez gratuitement les offres adaptées à votre métier</p>
    <button class="btn-primary" routerLink="/cac/comparaison">Comparer les offres</button>
  </div>
</app-page-layout>
